import { useState, useContext, useEffect } from "react";
import styles from "./styles.module.css";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { TextField, Grid, FormControl, Select, MenuItem, Checkbox, FormControlLabel } from "@mui/material";
import { PRODUCT_TYPE } from "const";
import ItemService from "services/ItemService";
import toast from "react-hot-toast";
import Loader from "../../utils/Loading";
import ButtonRed from "../../utils/CustomFields/ButtonRed";
import ButtonGray from "../../utils/CustomFields/ButtonGray";
import CustomTextField from "../../utils/CustomFields/CustomTextField";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ProductContext from "context/ProductContext";
import ShopContext from "context/ShopContext";
import { getApiErrorMessage } from "../../../utils/errorHandlingMessageUtils";

const ITEM_TYPE_OPTIONS = [
  { value: PRODUCT_TYPE.INVENTORY, label: "Inventory" },
  { value: PRODUCT_TYPE.SERVICE, label: "Service" },
  { value: PRODUCT_TYPE.PRODUCTION, label: "Production" },
];

const dropdownSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#f9f9f9",
    "& fieldset": { borderColor: "#ccc" },
    "&:hover fieldset": { borderColor: "#888" },
    "&.Mui-focused fieldset": { borderColor: "#333", borderWidth: "2px" },
  },
  "& .MuiSelect-select": { padding: "10px 14px", fontSize: "14px" },
};

const descSx = {
  "& .MuiInputLabel-root": { color: "#7c7c7d !important" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#7c7c7d !important" },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    padding: "15px 12px 0",
    "& fieldset": { borderColor: "#CDCDCF" },
    "&:hover fieldset": { borderColor: "#CDCDCF" },
    "&.Mui-focused fieldset": { borderColor: "#333 !important", borderWidth: "1px !important" },
  },
  "& .MuiInputBase-input": { padding: 0 },
};

const getInitialFormState = (initialEan) => ({
  name: "",
  category: null,
  price: "",
  mrp: "",
  cost: "",
  unit: "",
  itemType: PRODUCT_TYPE.INVENTORY,
  hsn: "",
  ean: initialEan || "",
  tax: "",
  additionalTax: "",
  isAvailableForSale: true,
  description: "",
  stock: "",
  order: "",
});

const TextFieldRow = ({ placeholder, value, onChange, type = "text" }) => (
  <Grid item xs={12} className={styles.textFieldArea}>
    <CustomTextField placeholder={placeholder} value={value} onChange={onChange} type={type} />
  </Grid>
);

const NumRow = ({ placeholder, value, onChange }) => (
  <Grid item xs={12} className={styles.textFieldArea}>
    <CustomTextField placeholder={placeholder} value={value} onChange={onChange} type="number" />
  </Grid>
);

const renderCategoryItems = (categories) => {
  if (!categories?.length) return <MenuItem disabled>No categories</MenuItem>;
  return categories.map((c) => (
    <MenuItem key={c.id} value={c.id}>
      {c.name}
    </MenuItem>
  ));
};

function CategorySelect({ formData, updateField, categories }) {
  return (
    <Grid item xs={12} className={styles.textFieldArea}>
      <FormControl fullWidth sx={dropdownSx}>
        <Select
          value={formData.category?.id || ""}
          onChange={(e) =>
            updateField(
              "category",
              categories.find((c) => c.id === e.target.value),
            )
          }
          displayEmpty
          IconComponent={ExpandMoreIcon}
          renderValue={(s) => (s ? formData.category?.name : <span style={{ color: "#aaa" }}>Select Category</span>)}
        >
          {renderCategoryItems(categories)}
        </Select>
      </FormControl>
    </Grid>
  );
}

function BasicDetailsSection({ formData, updateField, categories }) {
  return (
    <>
      <TextFieldRow
        placeholder="Item name"
        value={formData.name}
        onChange={(e) => updateField("name", e.target.value)}
      />
      <CategorySelect formData={formData} updateField={updateField} categories={categories} />
    </>
  );
}

function PricingSection({ formData, updateField }) {
  return (
    <>
      <NumRow placeholder="Price" value={formData.price} onChange={(e) => updateField("price", e.target.value)} />
      <NumRow placeholder="MRP" value={formData.mrp} onChange={(e) => updateField("mrp", e.target.value)} />
      <NumRow placeholder="Cost" value={formData.cost} onChange={(e) => updateField("cost", e.target.value)} />
    </>
  );
}

function UnitDropdown({ formData, updateField, selectedShop }) {
  return (
    <Grid item xs={12} className={styles.textFieldArea}>
      <FormControl fullWidth sx={dropdownSx}>
        <Select
          value={formData.unit}
          onChange={(e) => updateField("unit", e.target.value)}
          displayEmpty
          IconComponent={ExpandMoreIcon}
          renderValue={(s) => s || <span style={{ color: "#aaa" }}>Select Unit</span>}
        >
          {selectedShop?.units ? (
            selectedShop.units.split(",").map((u) => (
              <MenuItem key={u.trim()} value={u.trim()}>
                {u.trim()}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No units</MenuItem>
          )}
        </Select>
      </FormControl>
    </Grid>
  );
}

function ItemTypeDropdown({ formData, updateField }) {
  return (
    <Grid item xs={12} className={styles.textFieldArea}>
      <FormControl fullWidth sx={dropdownSx}>
        <Select
          value={formData.itemType}
          onChange={(e) => updateField("itemType", e.target.value)}
          displayEmpty
          IconComponent={ExpandMoreIcon}
          renderValue={(s) =>
            s ? (
              ITEM_TYPE_OPTIONS.find((o) => o.value === s)?.label
            ) : (
              <span style={{ color: "#aaa" }}>Select Item Type</span>
            )
          }
        >
          {ITEM_TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );
}

function InventoryAndTaxSection({ formData, updateField }) {
  return (
    <>
      {formData.itemType === PRODUCT_TYPE.INVENTORY && (
        <NumRow placeholder="Stock" value={formData.stock} onChange={(e) => updateField("stock", e.target.value)} />
      )}
      <TextFieldRow placeholder="HSN code" value={formData.hsn} onChange={(e) => updateField("hsn", e.target.value)} />
      <TextFieldRow placeholder="Barcode" value={formData.ean} onChange={(e) => updateField("ean", e.target.value)} />
      <NumRow placeholder="Tax %" value={formData.tax} onChange={(e) => updateField("tax", e.target.value)} />
      <NumRow
        placeholder="Additional tax"
        value={formData.additionalTax}
        onChange={(e) => updateField("additionalTax", e.target.value)}
      />
      <NumRow
        placeholder="Display Order"
        value={formData.order}
        onChange={(e) => updateField("order", e.target.value)}
      />
    </>
  );
}

function AvailableSaleCheckbox({ formData, updateField }) {
  return (
    <Grid item xs={12} className={styles.textFieldArea}>
      <FormControlLabel
        control={
          <Checkbox
            checked={formData.isAvailableForSale}
            onChange={(e) => updateField("isAvailableForSale", e.target.checked)}
            sx={{ color: "#ccc", "&.Mui-checked": { color: "#e50218" } }}
          />
        }
        label="Available for sale"
        sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px", color: "#2b2b2b", fontWeight: 500 } }}
      />
    </Grid>
  );
}

function DescriptionInput({ formData, updateField }) {
  return (
    <Grid item xs={12} className={styles.textFieldArea}>
      <TextField
        fullWidth
        multiline
        rows={5}
        placeholder="Description"
        value={formData.description}
        onChange={(e) => updateField("description", e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={descSx}
      />
    </Grid>
  );
}

const buildPayload = (f) => ({
  name: f.name.trim(),
  category: f.category?.id,
  unit: f.unit.trim(),
  price: Number(f.price),
  mrp: f.mrp ? Number(f.mrp) : 0,
  cost: f.cost ? Number(f.cost) : 0,
  type: f.itemType || PRODUCT_TYPE.INVENTORY,
  hsn: f.hsn.trim() || null,
  ean: f.ean.trim() || "nil",
  tax: f.tax ? Number(f.tax) : 0,
  cess: f.additionalTax ? Number(f.additionalTax) : 0,
  is_available: f.isAvailableForSale,
  is_available_on_cart: f.isAvailableForSale,
  stock: f.stock ? Number(f.stock) : 0,
  least_price: 0,
  description: f.description.trim() || null,
  order: f.order ? Number(f.order) : null,
});

function useAddItemData(initialEan, open, onClose) {
  const [formData, setFormData] = useState(getInitialFormState(""));
  const [isLoading, setIsLoading] = useState(false);
  const { addItem } = useContext(ProductContext);
  useEffect(() => {
    if (open) setFormData(getInitialFormState(initialEan));
  }, [open, initialEan]);

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.unit.trim() || !formData.price) return toast.error("Missing fields");
    setIsLoading(true);
    try {
      addItem(await ItemService.addItem(buildPayload(formData)));
      setFormData(getInitialFormState(initialEan));
      toast.success("Product created successfully!");
      onClose();
    } catch (error) {
      toast.error(`Failed to create item: ${getApiErrorMessage(error)}`);
    } finally {
      setIsLoading(false);
    }
  };
  const updateField = (f, v) => setFormData((p) => ({ ...p, [f]: v }));
  return { formData, updateField, isLoading, handleCreate };
}

function PopupHeader({ onClose }) {
  return (
    <>
      <div className={styles.closeIcoSection}>
        <CloseRoundedIcon onClick={onClose} className={styles.icon} />
      </div>
      <div className={styles.headerSection} style={{ paddingBottom: "15px" }}>
        <div>
          <h2>Add new item</h2>
          <h4>Add and organize item with ease</h4>
        </div>
      </div>
    </>
  );
}

function PopupActions({ onClose, handleCreate, isLoading }) {
  return (
    <div className={styles.actionButtons}>
      <ButtonGray onClick={onClose}>Cancel</ButtonGray>
      <ButtonRed onClick={handleCreate} disabled={isLoading}>
        Create
      </ButtonRed>
    </div>
  );
}

function AddItemPopup({ open, onClose, categories, initialEan = "" }) {
  const { formData, updateField, isLoading, handleCreate } = useAddItemData(initialEan, open, onClose);
  const { selectedShop } = useContext(ShopContext);
  if (!open) return null;

  return (
    <div className={styles.popupOverlay}>
      <Loader isOpen={isLoading} />
      <div className={styles.popupContainer}>
        <PopupHeader onClose={onClose} />
        <Grid container className={styles.CategoryWrapper}>
          <BasicDetailsSection formData={formData} updateField={updateField} categories={categories} />
          <UnitDropdown formData={formData} updateField={updateField} selectedShop={selectedShop} />
          <PricingSection formData={formData} updateField={updateField} />
          <ItemTypeDropdown formData={formData} updateField={updateField} />
          <InventoryAndTaxSection formData={formData} updateField={updateField} />
          <AvailableSaleCheckbox formData={formData} updateField={updateField} />
          <DescriptionInput formData={formData} updateField={updateField} />
        </Grid>
        <PopupActions onClose={onClose} handleCreate={handleCreate} isLoading={isLoading} />
      </div>
    </div>
  );
}

export default AddItemPopup;
